import React, { FC, useMemo } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { AdvancedSelect } from '../../../widgets/select';
import { T } from 'src/shared/helpers';
import { searchTemplateCategoryQuery } from './__generated__/searchTemplateCategoryQuery.graphql';

interface SearchCategoryProps {
    value: number | null;
    onChange: (id: string | null) => void;
}

type OptionType = {
    readonly label: string;
    readonly value: string;
};

type CategoryType = {
    readonly label: string;
    readonly options: OptionType[];
};

const SearchTemplateCategory: FC<SearchCategoryProps> = ({ value, onChange }) => {
    const query = useLazyLoadQuery<searchTemplateCategoryQuery>(
        graphql`
            query searchTemplateCategoryQuery {
                productMacroCategories(isLibrary: true) {
                    productMacroCategoryId
                    name
                    categories {
                        productCategoryId
                        name
                    }
                }
            }
        `,
        {}
    );

    // Build groups and items structure
    const options = useMemo(
        () =>
            query.productMacroCategories?.map((x) => {
                if (x.categories.length > 0) {
                    return {
                        label: x.name,
                        options: [
                            ...x.categories.map((j) => {
                                return {
                                    label: j.name,
                                    value: 'c_' + j.productCategoryId.toString()
                                };
                            })
                        ]
                    };
                } else {
                    return {
                        label: x.name,
                        options: []
                    };
                }
            }) as CategoryType[],
        [query.productMacroCategories]
    );

    // Flat list of all options, used for making the search of the selected value simple with a find
    const allOptions = useMemo(() => options?.reduce<OptionType[]>((prev, curr) => (prev = prev.concat(curr.options)), []), [options]);

    return (
        <AdvancedSelect
            isClearable
            options={options}
            value={allOptions?.find((x) => x.value == `c_${value}`)}
            placeholder={T._('Filter by category...', 'Admin')}
            onChange={(option) => {
                onChange(option ? option.value : null);
            }}
        />
    );
};

export default SearchTemplateCategory;
